import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MultilineTextSkeleton } from '@/components/ui/content-loading-skeletons';
import { useCarePathwayMutations } from '@/features/patient-record/hooks/useCarePathwayMutations';
import { tenantQueries } from '@/features/patient-record/queries';

interface AssignPathwayDialogProps {
  readonly guid: string;
  readonly onClose: () => void;
}

export function AssignPathwayDialog({ guid, onClose }: AssignPathwayDialogProps): ReactElement {
  const { t } = useTranslation();
  const templatesQuery = useQuery(tenantQueries.carePathwayTemplates());
  const { assign } = useCarePathwayMutations(guid);
  const templates = templatesQuery.data ?? [];

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-lg" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('patient.carePathways.assign')}</DialogTitle>
          <DialogDescription>
            {t('patient.carePathways.assignDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {templatesQuery.isPending && (
            <MultilineTextSkeleton ariaLabel={t('patient.carePathways.templates.loading')} />
          )}
          {templatesQuery.isError && (
            <p className="text-sm text-destructive">
              {t('patient.carePathways.templates.error')}
            </p>
          )}
          {templates
            .filter((template) => template.state === 'ACTIVE')
            .map((template) => (
              <button
                key={template.id}
                type="button"
                disabled={assign.isPending}
                onClick={() => {
                  assign.mutate(
                    {
                      templateId: template.id,
                      templateName: template.name,
                      templateSteps: template.steps,
                    },
                    { onSuccess: onClose },
                  );
                }}
                className="w-full text-left rounded-md border border-border p-3 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-60 disabled:cursor-wait"
              >
                <p className="text-sm font-medium text-card-foreground">{template.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {template.description}
                </p>
                <p className="text-xs text-muted-foreground mt-1.5 tabular-nums">
                  {t('patient.carePathways.stepsTotal', { count: template.steps.length })}
                </p>
              </button>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
